import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2, MessageSquare, ExternalLink, Copy, Check, Smartphone, Clock, Link2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal de prévisualisation SMS Fallback
 * Affiche le SMS qui sera envoyé si RCS échoue
 */
export default function SMSPreviewModal({ 
  open, 
  onOpenChange, 
  messageContent,
  senderName = "B2RCS",
  isMarketing = true
}) {
  const [loading, setLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [customSlug, setCustomSlug] = useState('');
  const [expiryDuration, setExpiryDuration] = useState('72h');
  const [copied, setCopied] = useState(false);

  // Déterminer le type RCS depuis le contenu
  const getRcsType = () => {
    if (!messageContent) return 'text';
    const type = messageContent.message_type?.toLowerCase();
    if (type === 'richcard' || type === 'rich_card') return 'rich_card';
    if (type === 'carousel') return 'carousel';
    if (type === 'image') return 'image';
    if (type === 'file') return 'file';
    return 'text';
  };

  // Convertir le contenu RCS pour l'API
  const formatContentForApi = () => {
    if (!messageContent?.content) return {};
    
    const content = messageContent.content;
    const type = getRcsType();
    
    if (type === 'rich_card') {
      return {
        richCard: {
          standaloneCard: {
            cardContent: {
              title: content.title || content.cardTitle || '',
              description: content.description || content.cardDescription || '',
              media: content.media ? { contentInfo: { fileUrl: content.media.url || content.media.fileUrl || content.mediaUrl } } : 
                     content.mediaUrl ? { contentInfo: { fileUrl: content.mediaUrl } } : {},
              suggestions: content.suggestions || []
            }
          }
        }
      };
    }
    
    if (type === 'carousel') {
      const cards = content.cards || content.carouselCards || [];
      return {
        richCard: {
          carouselCard: {
            cardContents: cards.map(card => ({
              title: card.title || '',
              description: card.description || '',
              media: card.media ? { contentInfo: { fileUrl: card.media.url || card.media.fileUrl || card.mediaUrl } } : 
                     card.mediaUrl ? { contentInfo: { fileUrl: card.mediaUrl } } : {},
              suggestions: card.suggestions || []
            }))
          }
        }
      };
    }
    
    if (type === 'image') {
      return {
        image_url: content.media?.url || content.media?.fileUrl || content.imageUrl || content.mediaUrl || '',
        text: content.text || content.caption || '',
        suggestions: content.suggestions || []
      };
    }
    
    // Type text par défaut
    return {
      text: content.text || (typeof content === 'string' ? content : ''),
      suggestions: content.suggestions || []
    };
  };

  const handleGeneratePreview = async () => {
    setLoading(true);
    setPreviewResult(null);

    try {
      const response = await fetch(`${API_URL}/api/sms-fallback/test-conversion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rcs_type: getRcsType(),
          sender_name: senderName,
          is_marketing: isMarketing,
          custom_slug: customSlug || undefined,
          expiry_duration: expiryDuration,
          content: formatContentForApi()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewResult(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyText = () => {
    if (previewResult?.conversion_result?.sms_text) {
      navigator.clipboard.writeText(previewResult.conversion_result.sms_text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('SMS copié !');
    }
  };

  const handleOpenLandingPage = () => {
    if (previewResult?.conversion_result?.landing_page_url) {
      window.open(previewResult.conversion_result.landing_page_url, '_blank');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-amber-500" />
            Prévisualisation SMS Fallback
          </DialogTitle>
          <DialogDescription>
            Aperçu du SMS qui sera envoyé si le destinataire n'est pas compatible RCS
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Options de personnalisation */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="custom-slug" className="flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Slug personnalisé (optionnel)
              </Label>
              <Input
                id="custom-slug"
                placeholder="promo-ete-2025"
                value={customSlug}
                onChange={(e) => setCustomSlug(e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                URL: /api/m/{customSlug || '[auto]'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiry" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Durée de validité du lien
              </Label>
              <Select value={expiryDuration} onValueChange={setExpiryDuration}>
                <SelectTrigger id="expiry">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="72h">72 heures (défaut)</SelectItem>
                  <SelectItem value="7j">7 jours</SelectItem>
                  <SelectItem value="illimite">Illimité (1 an)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Info Marketing/Transactionnel */}
          <div className={`flex items-center gap-3 p-3 rounded-lg ${isMarketing ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
            <AlertTriangle className={`h-5 w-5 ${isMarketing ? 'text-amber-500' : 'text-blue-500'}`} />
            <div className="text-sm">
              <span className="font-medium">
                {isMarketing ? 'Message Marketing' : 'Message Transactionnel'}
              </span>
              <span className="text-slate-600 ml-2">
                {isMarketing 
                  ? '→ Le lien stopsms.io sera ajouté automatiquement (RGPD)'
                  : '→ Pas de lien STOP (message de service)'
                }
              </span>
            </div>
          </div>

          {/* Bouton Générer */}
          <Button
            onClick={handleGeneratePreview}
            disabled={loading || !messageContent}
            className="w-full bg-amber-500 hover:bg-amber-600"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Génération en cours...
              </>
            ) : (
              <>
                <Smartphone className="h-4 w-4 mr-2" />
                Générer la prévisualisation SMS
              </>
            )}
          </Button>

          {/* Résultat de la prévisualisation */}
          {previewResult && (
            <div className="space-y-4">
              {/* Mockup SMS */}
              <div className="bg-slate-900 rounded-2xl p-4 max-w-sm mx-auto">
                <div className="bg-slate-800 rounded-xl p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-700">
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm">
                      {senderName[0]?.toUpperCase()}
                    </div>
                    <span className="text-white font-medium">{senderName}</span>
                    <Badge variant="outline" className="ml-auto text-xs border-amber-500 text-amber-400">
                      SMS
                    </Badge>
                  </div>
                  
                  {/* Message */}
                  <div className="bg-slate-700 rounded-lg p-3">
                    <p className="text-white text-sm whitespace-pre-wrap leading-relaxed">
                      {previewResult.conversion_result.sms_text}
                    </p>
                  </div>

                  {/* Meta info */}
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{previewResult.conversion_result.char_count} caractères</span>
                    <span>{previewResult.conversion_result.sms_parts} SMS</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={handleCopyText}>
                  {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copied ? 'Copié !' : 'Copier le texte'}
                </Button>
                
                {previewResult.conversion_result.landing_page_url && (
                  <Button variant="outline" size="sm" onClick={handleOpenLandingPage}>
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Voir la landing page
                  </Button>
                )}
              </div>

              {/* Détails techniques */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <h4 className="font-medium text-slate-700">Détails de la conversion</h4>
                <div className="grid grid-cols-2 gap-2 text-slate-600">
                  <div>Type RCS original:</div>
                  <div className="font-mono">{previewResult.conversion_result.original_type}</div>
                  
                  <div>Catégorie:</div>
                  <div>
                    <Badge variant={previewResult.conversion_result.message_category === 'marketing' ? 'default' : 'secondary'}>
                      {previewResult.conversion_result.message_category}
                    </Badge>
                  </div>
                  
                  <div>Contenu riche:</div>
                  <div>{previewResult.conversion_result.has_rich_content ? '✅ Oui (landing page)' : '❌ Non'}</div>
                  
                  <div>STOP SMS:</div>
                  <div>{previewResult.conversion_result.has_stop_sms ? '✅ Inclus' : '❌ Non requis'}</div>
                  
                  {previewResult.conversion_result.landing_page_url && (
                    <>
                      <div>URL landing:</div>
                      <div className="font-mono text-xs break-all text-indigo-600">
                        {previewResult.conversion_result.landing_page_url}
                      </div>
                    </>
                  )}
                </div>
                
                {previewResult.conversion_result.conversion_notes?.length > 0 && (
                  <div className="pt-2 border-t border-slate-200 mt-2">
                    <p className="text-slate-500 text-xs">
                      Notes: {previewResult.conversion_result.conversion_notes.join(' • ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
